import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import _styles from "./assets/stylesheets/embed.css?url";
import _fonts from "./assets/stylesheets/base/fonts.css?url";

// Resolve to absolute URLs so the embed works cross-origin
const styles = new URL(_styles, import.meta.url).href;
const fonts = new URL(_fonts, import.meta.url).href;
import { Survey } from "./views/statement_survey";
import { apiClient } from "./client";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
  integrations: [
    Sentry.thirdPartyErrorFilterIntegration({
      // This key needs to exactly match the configuration in vite-embed.config.js
      filterKeys: ["tree-amai-2026"],
      behaviour: "drop-error-if-exclusively-contains-third-party-frames",
    }),
  ],
});

// NOTE: We're assuming that this font is already present when embedding
class AmaiEmbed extends HTMLElement {
  // Static methods
  static tagName = "amai-embed";

  static register(tagName = this.tagName, registry) {
    if ("customElements" in window) {
      (registry || customElements).define(tagName, this);
    }
  }

  #fathomScript = null;

  // Instance methods
  connectedCallback() {
    this.#loadFonts();
    window.addEventListener("resize", this.setElementHeight.bind(this));
    window.addEventListener("blur", this.setElementHeight.bind(this));
    this.#insertFathomScript();
    this.setElementHeight();
    this.shadow = this.attachShadow({ mode: "closed" });
    this.renderElement();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setElementHeight.bind(this));
    window.removeEventListener("blur", this.setElementHeight.bind(this));
    this.#fathomScript?.remove();
  }

  renderElement() {
    ReactDOM.createRoot(this.shadow).render(
      <div id="amai-root" style={{ fontFamily: "var(--font-body)" }}>
        {/* We hide the element itself, until our CSS is loaded */}
        <style>{":where(#amai-root) { visibility: hidden; }"}</style>
        <link rel="stylesheet" href={this.stylesheetURL} />
        <Survey apiClient={apiClient} />
      </div>,
    );
  }

  setElementHeight() {
    this.style.setProperty("--app-height", `${window.innerHeight}px`);
  }

  get stylesheetURL() {
    return styles;
  }

  #loadFonts() {
    const node = document.createElement("link");
    node.rel = "stylesheet";
    node.href = fonts;
    this.before(node);
  }

  #insertFathomScript() {
    if (import.meta.env.VITE_FATHOM_ID) {
      this.#fathomScript = document.createElement("script");
      this.#fathomScript.src = "https://cdn-eu.usefathom.com/script.js";
      this.#fathomScript.defer = true;
      this.#fathomScript.setAttribute("data-auto", "false");
      this.#fathomScript.setAttribute(
        "data-site",
        import.meta.env.VITE_FATHOM_ID,
      );
      this.before(this.#fathomScript);
    }
  }
}

AmaiEmbed.register();
